import { useSnackbar } from 'notistack';
import { put } from '../Shared/ajax';
import { useState, useContext, useRef, useEffect, useReducer } from 'react';
import { Grid, Typography } from '@mui/material';
import {
  LastUpdatedInfo,
  WorkflowEngineActionKind,
  WorkflowEngineContext,
  WorkflowEngineMode
} from '../WorkflowEngine/workflowEngineReducer';
import PublishDialog from './PublishDialog';
import UnpublishedView from './WorkflowSubHeaderViews/UnpublishedView';
import PublishedView from './WorkflowSubHeaderViews/PublishedView';
import RoleSelector from './RoleSelector';
import BackToLandingButton from '../Shared/BackToLandingButton';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import IconButton from '@mui/material/IconButton';
import { InputTextField } from '@abbott-components/core';
import { Stack } from '@mui/material';

interface UpdatePageRequest {
  workflowExternalId: string;
  workflowName: string;
}

interface UpdatePageResponse {
  externalId: string;
  lastUpdatedDate: string;
  lastUpdatedBy: string;
  metadata: string;
}

export default () => {
  const [openPublishDialog, setOpenPublishDialog] = useState<boolean>(false);
  const { state, dispatch } = useContext(WorkflowEngineContext);
  const [isEditName, setIsEditName] = useState(false);
  const workflowNameInputRef = useRef<HTMLInputElement>(null);
  const { externalId, versionData, mode, workflowName } = state;
  const [currentWorkflowName, setCurrentWorkflowName] = useState(workflowName);

  const { enqueueSnackbar } = useSnackbar();

  const handleOpenPublishDialog = () => {
    setOpenPublishDialog(true);
  };

  const handleClosePublishDialog = () => {
    setOpenPublishDialog(false);
  };

  const handleOnEditClick = () => {
    setIsEditName(true);
  };

  const handleOnChange = () => {
    const temp = workflowNameInputRef.current?.value;
    temp ? (temp != workflowName ? setCurrentWorkflowName(temp) : null) : null;
  };

  const handleSaveNameChange = () => {
    setIsEditName(false);
    if (currentWorkflowName !== workflowName) {
      dispatch({
        type: WorkflowEngineActionKind.WORKFLOW_NAME_UPDATE,
        payload: currentWorkflowName
      });
      updateWorkflowName(externalId, currentWorkflowName);
    }
  };

  const updateWorkflowName = async (
    externalId: string,
    workflowName: string
  ) => {
    try {
      const url = `DynamicWorkflow/WorkflowName`;
      const response = await put<UpdatePageResponse, UpdatePageRequest>(url, {
        workflowExternalId: externalId,
        workflowName: workflowName
      });

      enqueueSnackbar('Configuration successfully saved!', {
        variant: 'success'
      });

      dispatch({
        type: WorkflowEngineActionKind.SET_LAST_UPDATED_INFO,
        payload: {
          externalId: response.externalId,
          lastUpdatedDate: response.lastUpdatedDate,
          lastUpdatedBy: response.lastUpdatedBy,
          metadata: response.metadata
        } as LastUpdatedInfo
      });
    } catch (err) {
      const errorMsg = (err as Error).message;
      enqueueSnackbar(errorMsg, { variant: 'error' });
    }
  };

  useEffect(() => {
    workflowNameInputRef.current?.focus();
  }, [isEditName]);

  return (
    <>
      <Grid
        container
        direction="row"
        alignItems="center"
        sx={{
          backgroundColor:
            mode === WorkflowEngineMode.MAPPER ? '#F1F1F1' : 'white',
          paddingTop: '80px',
          paddingLeft: '30px',
          paddingRight: '30px'
        }}
      >
        <Grid item md={1}>
          <BackToLandingButton />
        </Grid>
        <Grid item md={7}>
          {isEditName === true ? (
            <Stack direction="row" spacing={2}>
              <InputTextField
                inputRef={workflowNameInputRef}
                name="workflowName"
                value={currentWorkflowName}
                isDisabled={false}
                onChange={handleOnChange}
                onBlur={handleSaveNameChange}
              />
              <IconButton onClick={handleSaveNameChange}>
                <SaveIcon fontSize="medium" />
              </IconButton>
            </Stack>
          ) : (
            <Typography
              variant="h1"
              component="h1"
              gutterBottom
              sx={{ paddingLeft: '20px' }}
            >
              {workflowName}{' '}
              {versionData?.isPublished ? null : mode ===
                WorkflowEngineMode.BUILD ? (
                <IconButton onClick={handleOnEditClick}>
                  <EditIcon fontSize="medium" />
                </IconButton>
              ) : null}
            </Typography>
          )}
        </Grid>
        <Grid
          container
          item
          alignItems="center"
          justifyContent="flex-end"
          md={4}
        >
          {mode === WorkflowEngineMode.PREVIEW && <RoleSelector />}
          {versionData?.isPublished ? (
            <PublishedView handleOpenPublishDialog={handleOpenPublishDialog} />
          ) : (
            <UnpublishedView
              handleOpenPublishDialog={handleOpenPublishDialog}
            />
          )}
        </Grid>
      </Grid>
      <PublishDialog
        handleClose={handleClosePublishDialog}
        open={openPublishDialog}
      />
    </>
  );
};
